import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";
import GetUserProfiles from "../team/queries/GetUserProfiles";
import InviteLeagueCoordinatorMutation from "../team/mutations/InviteLeagueCoordinator";

const ROLE_COORDINATOR = 6;

export default function InviteCoordinatorModal({
    show,
    onClose,
    profileId,
    organizationId,
    organizationName,
    colors,
}) {
    const { t } = useTranslation();
    const [getUserProfiles, { data, loading, error }] = GetUserProfiles();
    const [inviteCoordinator, { loading: isInviting }] = InviteLeagueCoordinatorMutation();
    const [invitingProfileId, setInvitingProfileId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [optimisticInvitedIds, setOptimisticInvitedIds] = useState([]);
    const lastSearchTermRef = useRef(null);
    const lastRequestKeyRef = useRef("");
    const pageSize = 10;

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (!show || !profileId || !organizationId) return;

        if (lastSearchTermRef.current !== debouncedSearchTerm) {
            lastSearchTermRef.current = debouncedSearchTerm;
            if (page !== 1) {
                setPage(1);
                return;
            }
        }

        const requestKey = `${profileId}:${debouncedSearchTerm}:${page}`;
        if (lastRequestKeyRef.current === requestKey) return;
        lastRequestKeyRef.current = requestKey;

        getUserProfiles({
            variables: {
                q: debouncedSearchTerm || null,
                page,
                pageSize,
            },
            fetchPolicy: "network-only",
        });
    }, [debouncedSearchTerm, getUserProfiles, organizationId, page, pageSize, profileId, show]);

    useEffect(() => {
        if (!show) {
            setSearchTerm("");
            setDebouncedSearchTerm("");
            setPage(1);
            setOptimisticInvitedIds([]);
            lastSearchTermRef.current = null;
            lastRequestKeyRef.current = "";
        }
    }, [show]);

    const profiles = useMemo(() => {
        const rawProfiles = Array.isArray(data?.sparePlayers?.objects)
            ? data.sparePlayers.objects
            : [];

        const seenIds = new Set();
        return rawProfiles.filter((profile) => {
            const currentProfileId = Number(profile?.id);
            if (!currentProfileId || seenIds.has(currentProfileId)) return false;
            seenIds.add(currentProfileId);
            return true;
        });
    }, [data]);

    const pagination = useMemo(
        () => ({
            page: Number(data?.sparePlayers?.page) || page,
            pages: Number(data?.sparePlayers?.pages) || 1,
            hasNext: Boolean(data?.sparePlayers?.hasNext),
            hasPrev: Boolean(data?.sparePlayers?.hasPrev),
        }),
        [data?.sparePlayers?.hasNext, data?.sparePlayers?.hasPrev, data?.sparePlayers?.page, data?.sparePlayers?.pages, page]
    );

    const optimisticInvitedSet = useMemo(
        () => new Set((optimisticInvitedIds || []).map((id) => Number(id))),
        [optimisticInvitedIds]
    );

    const isAlreadyInvitedToOrganization = (profile) => {
        const profileKey = Number(profile?.id);
        if (!profileKey) return false;
        if (optimisticInvitedSet.has(profileKey)) return true;

        const invites = Array.isArray(profile?.profileOrganizationsInvited)
            ? profile.profileOrganizationsInvited
            : [];

        return invites.some((invite) => Number(invite?.organization?.id) === Number(organizationId));
    };

    const handleAdd = async (selectedProfile) => {
        const selectedProfileId = Number(selectedProfile?.id);
        if (!profileId || !organizationId || !selectedProfileId) return;

        try {
            setInvitingProfileId(selectedProfileId);
            const { data: response } = await inviteCoordinator({
                variables: {
                    profileId: selectedProfileId,
                    teamId: Number(organizationId),
                    seasonId: 0,
                    role: ROLE_COORDINATOR,
                },
            });

            if (!response?.invitePlayer?.success) {
                toast.error(t("coordinatorPicker.addError"), {
                    autoClose: 3000,
                    position: "top-center",
                    closeOnClick: true,
                });
                return;
            }

            setOptimisticInvitedIds((prev) => {
                const current = new Set((prev || []).map((id) => Number(id)));
                current.add(selectedProfileId);
                return Array.from(current);
            });

            toast.success(t("coordinatorPicker.addSuccess"), {
                autoClose: 2500,
                position: "top-center",
                closeOnClick: true,
            });
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "coordinatorPicker.addError"), {
                autoClose: 3000,
                position: "top-center",
                closeOnClick: true,
            });
        } finally {
            setInvitingProfileId(null);
        }
    };

    if (!show) return null;

    return (
        <div
            className="modal d-block"
            role="dialog"
            aria-modal="true"
            style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
        >
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                <div className="modal-content" style={{ borderRadius: 14 }}>
                    <div
                        className="modal-header"
                        style={{
                            backgroundColor: colors?.primary,
                            color: colors?.textOnPrimary,
                        }}
                    >
                        <h5 className="modal-title mb-0">
                            {t("coordinatorPicker.title", { name: organizationName || "" })}
                        </h5>
                        <button
                            type="button"
                            className="close"
                            onClick={onClose}
                            aria-label={t("common.cancel")}
                            disabled={isInviting}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div className="modal-body">
                        <div className="form-group">
                            <div className="input-group">
                                <div className="input-group-prepend">
                                    <span className="input-group-text" aria-hidden="true">
                                        <i className="fas fa-search" />
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t("coordinatorPicker.searchPlaceholder")}
                                    disabled={isInviting}
                                />
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-3">{t("common.loading")}</div>
                        ) : error ? (
                            <div className="alert alert-danger mb-0">{t("coordinatorPicker.loadError")}</div>
                        ) : !profiles.length ? (
                            <div className="alert alert-light mb-0">
                                {debouncedSearchTerm
                                    ? t("coordinatorPicker.noSearchResults")
                                    : t("coordinatorPicker.noProfiles")}
                            </div>
                        ) : (
                            <div>
                                <div className="table-responsive">
                                    <table className="table table-striped mb-0">
                                        <thead>
                                            <tr>
                                                <th>{t("coordinatorPicker.profile")}</th>
                                                <th className="text-right">{t("playersList.actions")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {profiles.map((profile) => {
                                                const profileKey = Number(profile.id);
                                                const alreadyAdded = isAlreadyInvitedToOrganization(profile);
                                                const isRowInviting = invitingProfileId === profileKey;

                                                return (
                                                    <tr key={profile.id}>
                                                        <td>{profile.fullName}</td>
                                                        <td className="text-right">
                                                            <button
                                                                type="button"
                                                                className="btn btn-kbola btn-sm"
                                                                onClick={() => handleAdd(profile)}
                                                                disabled={alreadyAdded || isInviting}
                                                            >
                                                                {isRowInviting
                                                                    ? t("common.loading")
                                                                    : alreadyAdded
                                                                        ? t("coordinatorPicker.alreadyAdded")
                                                                        : t("coordinatorPicker.add")}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="d-flex align-items-center justify-content-between mt-3">
                                    <small className="text-muted">
                                        {t("teamMemberPicker.pageInfo", {
                                            page: pagination.page,
                                            pages: pagination.pages,
                                        })}
                                    </small>

                                    <div className="btn-group" role="group" aria-label="pagination">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                            disabled={!pagination.hasPrev || loading || isInviting}
                                        >
                                            {t("common.previous")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setPage((prev) => prev + 1)}
                                            disabled={!pagination.hasNext || loading || isInviting}
                                        >
                                            {t("common.next")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={onClose}
                            disabled={isInviting}
                        >
                            {t("common.cancel")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

